import * as React from 'react';

import {useQuery, useApolloClient} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    GetAudience as GetAudienceQuery,
    GetAudienceVariables as GetAudienceQueryVariables,
    Audience,
    AudienceStub,
} from 'nutshell-graphql-types';

import AUDIENCE_STUB_FRAGMENT from './graphql/fragments/audience-stub-fragment.graphql';
import GET_AUDIENCE from './graphql/queries/get-audience.graphql';

type Props = {
    audienceId: string;
    children: (arg1: {
        audience: Audience | null | undefined;
        audienceStub: AudienceStub | null | undefined;
        isLoading: boolean;
        error: string | null | undefined;
    }) => React.ReactElement;
};

export function AudienceQuery(props: Props) {
    const client = useApolloClient();
    let audienceStub: AudienceStub | null | undefined;
    try {
        // Not sure why, but this sometimes fails when viewing an audience
        // without viewing the list first. Therefore, we need to try/catch it.
        // If we do get an error though, we don't really need to do anything
        // with it. We just keep `audienceStub` set to undefined.
        audienceStub = client.readFragment({
            id: props.audienceId,
            fragmentName: 'AudienceStub',
            fragment: AUDIENCE_STUB_FRAGMENT,
        });
    } catch {
        // Empty
    }

    const {
        data,
        error,
        loading: isLoading,
    } = useQuery<GetAudienceQuery, GetAudienceQueryVariables>(GET_AUDIENCE, {
        variables: {audienceId: props.audienceId},
        notifyOnNetworkStatusChange: true,
    });

    const audience = data ? data.audience : null;

    return props.children({
        audience,
        audienceStub,
        // @ts-expect-error
        error: safelyGetGraphQLErrorMessage(error),
        isLoading,
    });
}
